import { useMemo } from 'react'
import type { Stage } from '@/components/shared/stage-status/types'
import { parseStatusUpdate } from '@/components/shared/timeline/noteParser'
import { orderStatusValueToLabel } from '@/constants/statuses'
import type { TimelineEvent } from '@/types/timeline'
import { useTimelineQuery } from '@/services/timeline'

function timelineEventsToStages(events: TimelineEvent[], creationDate?: string): Stage[] {
  const statusUpdates = events
    .filter(e => e.noteType === 'status_update')
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

  // Same rationale as useMedReqStatusStages: orders created before timeline
  // tracking was deployed have no status_update entries. Synthesise an initial
  // "Pending Payment" event from the order's creation date so the timeline
  // is never empty. We check statusUpdates (not events) because the timeline
  // may contain non-status events (e.g. field updates) that shouldn't prevent
  // the fallback.
  if (creationDate && !statusUpdates.length) {
    return [
      {
        id: 'initial',
        label: 'Pending Payment',
        status: 'complete',
        date: new Date(creationDate),
        type: 'Auto'
      }
    ]
  }

  if (statusUpdates.length === 0) return []

  const stages: Stage[] = []

  const firstParsed = parseStatusUpdate(statusUpdates[0].note)
  if (firstParsed) {
    stages.push({
      id: 'initial',
      label: orderStatusValueToLabel(firstParsed.previous),
      status: 'complete',
      date: statusUpdates[0].timestamp,
      type: statusUpdates[0].source === 'Manual' ? 'Manual' : 'Auto'
    })
  }

  for (const event of statusUpdates) {
    const parsed = parseStatusUpdate(event.note)
    if (!parsed) continue
    stages.push({
      id: `stage-${stages.length}`,
      label: orderStatusValueToLabel(parsed.current),
      status: 'complete',
      date: event.timestamp,
      type: event.source === 'Manual' ? 'Manual' : 'Auto'
    })
  }

  if (stages.length > 0) {
    stages[stages.length - 1].status = 'current'
  }

  return stages
}

export function useOrderStatusStages(orderHash: string, creationDate?: string, enabled = true) {
  const { data: events = [], isLoading, isError } = useTimelineQuery(orderHash, 'order', enabled)

  const stages = useMemo(() => timelineEventsToStages(events, creationDate), [events, creationDate])

  return { stages, events, isLoading, isError }
}
